import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import {
  CONSENT_ACCEPTED,
  CONSENT_DECLINED,
  COOKIE_PREFS_EVENT,
  getConsent,
  loadClarity,
  setConsent,
} from '../../lib/clarity';

/**
 * CookieConsentBanner
 *
 * UK PECR Reg 6 / ICO guidance: non-essential cookies (analytics,
 * session recording) must be LOADED ONLY AFTER the visitor has
 * consented. Accept and decline must be equally prominent.
 *
 * Decision is persisted in localStorage under `zuriya_cookie_consent`
 * so returning visitors aren't nagged. The CookiePolicy page explains
 * what we set and how to change your mind.
 *
 * Essential cookies (session JWT, CSRF, Stripe PI id) continue to run
 * regardless; the banner is strictly for analytics.
 */
export default function CookieConsentBanner() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const existing = getConsent();
    if (!existing) {
      setOpen(true);
      return;
    }
    // Returning visitor who previously accepted: boot Clarity now
    // (the first-paint load in index.html is intentionally skipped
    // because consent is per-browser, not per-request).
    if (existing === CONSENT_ACCEPTED) loadClarity();
  }, []);

  // Footer "Cookie preferences" link fires COOKIE_PREFS_EVENT so a
  // user who declined on first visit can change their mind later.
  useEffect(() => {
    const reopen = () => setOpen(true);
    window.addEventListener(COOKIE_PREFS_EVENT, reopen);
    return () => window.removeEventListener(COOKIE_PREFS_EVENT, reopen);
  }, []);

  const accept = () => {
    setConsent(CONSENT_ACCEPTED);
    loadClarity();
    setOpen(false);
  };

  const decline = () => {
    setConsent(CONSENT_DECLINED);
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie preferences"
      data-testid="cookie-consent-banner"
      className="fixed inset-x-3 bottom-3 sm:inset-x-auto sm:bottom-6 sm:right-6 sm:max-w-sm z-[60] rounded-2xl bg-[#1A1A1A] text-white shadow-2xl border border-white/10 p-5"
    >
      <p className="font-heading text-base leading-snug">
        Cookies, briefly.
      </p>
      <p className="text-sm text-white/75 mt-2 leading-relaxed">
        We use a single essential cookie to keep you signed in. We would
        also like to use Microsoft Clarity to see which parts of the
        site people struggle with, so we can fix them. You decide.{' '}
        <Link to="/cookies" className="underline text-[#D4AF37] hover:text-white">
          Full cookie policy
        </Link>
        .
      </p>
      <div className="mt-4 flex gap-2">
        <Button
          onClick={decline}
          variant="outline"
          className="flex-1 bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white"
          data-testid="cookie-decline-btn"
        >
          Decline
        </Button>
        <Button
          onClick={accept}
          className="flex-1 bg-[#D4AF37] text-[#1A1A1A] hover:bg-[#C9A633]"
          data-testid="cookie-accept-btn"
        >
          Accept
        </Button>
      </div>
    </div>
  );
}
